import React, { useEffect, useState } from 'react';
import cdisImage from 'assets/images/projects/cdis.png';
import Heading from 'components/atoms/text/heading';
import ExternalLink from 'components/atoms/external-link';
import SectionWithLeftIcon from 'components/organisms/section/sectionWithLeftIcon';
import { useLocation, useNavigate } from 'react-router-dom';

type ProjectId = 'cdis-egypt' | 'soil-spaces';

const TABS: { id: ProjectId; label: string }[] = [
  { id: 'cdis-egypt', label: 'CDIS Egypt 2024' },
  { id: 'soil-spaces', label: 'Soil Spaces Dashboard' },
];

function CdisProject() {
  return (
    <SectionWithLeftIcon
      styles="md:!flex-col lg:!flex-row"
      iconStyles="!h-auto !w-[85%] xs:!w-[75%] sm:!w-[65%] md:w-[55%] lg:!w-[55%] xl:!w-[600px]"
      iconHeader={
        <div className="flex flex-col items-center gap-0 md:gap-2">
          <div className="flex items-center gap-1">
            <Heading text="CDIS Egypt 2024 Website" />
            <ExternalLink href="https://www.cdis-egypt.com" styles="md:!mt-[10px]" />
          </div>
          <div className="flex items-center gap-1 mb-5">
            <div className="text-sm md:text-md">Backend & QC engineer: Osama Ayman</div>
            <ExternalLink href="https://www.linkedin.com/in/osama-ibrahim-a117311b7/" styles="md:!mt-[5px]" />
          </div>
        </div>
      }
      icon={cdisImage}
      alt="CDIS Egypt 2024">
      <div className="h-64 overflow-auto md:h-full md:overflow-visible w-full flex flex-col pt-3">
        <div className="">
          <div className="text-lg font-semibold mb-2">My Responsibilities:</div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <ul className="list-decimal list-inside flex flex-col gap-3 md:gap-1">
              <li>
                <strong>Frontend Development:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Executed the frontend development of the entire website</li>
                  <li>Applied the UI design provided by the client with pixel-perfect precision</li>
                </ol>
              </li>
              <li>
                <strong>Client Interaction:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Gathered requirements from the client</li>
                  <li>Translated client requirements into technical tasks</li>
                  <li>
                    Implemented tasks independently or collaborated with the backend engineer, whom I personally hired,
                    for execution
                  </li>
                </ol>
              </li>
              <li>
                <strong>Project Management:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Managed the CDIS domain throughout the project lifecycle</li>
                  <li>
                    Established a testing subdomain for client User Acceptance Testing (UAT) prior to the website going
                    live
                  </li>
                </ol>
              </li>
              <li>
                <strong>Hiring and Collaboration:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Personally recruited and hired the backend engineer for the project.</li>
                  <li>
                    Collaborated with the backend engineer to ensure seamless integration of frontend and backend
                    components
                  </li>
                </ol>
              </li>
              <li>
                <strong>Deployment and Infrastructure:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Handled deployment to Amazon Web Services (AWS)</li>
                  <li>Managed DNS nameservers within AWS</li>
                  <li>
                    Ensured seamless migration of existing DNS records, particularly safeguarding active email
                    functionality
                  </li>
                </ol>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </SectionWithLeftIcon>
  );
}

function SoilSpacesProject() {
  return (
    <SectionWithLeftIcon
      styles="md:!flex-col lg:!flex-row"
      iconHeader={
        <div className="flex flex-col items-center gap-0 md:gap-2">
          <div className="flex items-center gap-1">
            <Heading text="Soil Spaces Admin Dashboard" wrapText />
          </div>
        </div>
      }
      iconElement={
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          src="/soil-spaces-overview.mp4"
          className="!h-auto !w-[85%] xs:!w-[75%] sm:!w-[65%] md:w-[55%] lg:!w-[55%] xl:!w-[600px] rounded-lg"
          controls
          playsInline
        />
      }
      alt="Soil Spaces Admin Dashboard">
      <div className="h-64 overflow-auto md:h-full md:overflow-visible w-full flex flex-col pt-3">
        <div className="">
          <div className="text-lg font-semibold mb-2">My Responsibilities:</div>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <ul className="list-decimal list-inside flex flex-col gap-3 md:gap-1">
              <li>
                <strong>Full Project Ownership:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Built the entire admin dashboard from scratch with no prior codebase</li>
                  <li>Gathered requirements directly from the client and translated them into technical tasks</li>
                </ol>
              </li>
              <li>
                <strong>Frontend Development:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>
                    Developed all modules independently: bookings, members, memberships, spaces, events, rewards, and
                    financial reports
                  </li>
                  <li>Built with React 19, TypeScript, Vite, Tailwind CSS, Zod, and Radix UI</li>
                  <li>Applied a modular, component-based architecture with reusable hooks and helpers</li>
                </ol>
              </li>
              <li>
                <strong>API Integration:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Integrated with a REST API using React Query for server state management</li>
                  <li>Implemented full CRUD support across all modules</li>
                </ol>
              </li>
              <li>
                <strong>Access Control:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Implemented role-based access control and protected routing</li>
                  <li>Enforced permissions across different admin roles</li>
                </ol>
              </li>
              <li>
                <strong>Deployment and Infrastructure:</strong>
                <ol className="list-[lower-roman] list-inside pl-4">
                  <li>Handled deployment to Amazon Web Services (AWS)</li>
                  <li>Configured the production environment and managed the deployment pipeline</li>
                </ol>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </SectionWithLeftIcon>
  );
}

export default function Projects() {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ProjectId>('cdis-egypt');

  useEffect(() => {
    const query = new URLSearchParams(search);
    const projectParam = query.get('project') as ProjectId | null;

    if (projectParam && TABS.some(t => t.id === projectParam)) {
      setActiveTab(projectParam);
      query.delete('project');
      navigate({ pathname, search: query.toString() }, { replace: true });
    }
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-black'
                : 'bg-transparent text-white border border-white/30 hover:border-white/60'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid">
        <div className={`col-start-1 row-start-1 ${activeTab === 'cdis-egypt' ? '' : 'invisible pointer-events-none'}`}>
          <CdisProject />
        </div>
        <div
          className={`col-start-1 row-start-1 ${activeTab === 'soil-spaces' ? '' : 'invisible pointer-events-none'}`}>
          <SoilSpacesProject />
        </div>
      </div>
    </div>
  );
}
